import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import tiffinService from '../services/tiffinService';

const TiffinRegistration = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('planId');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'USA',
    deliveryAddress: '',
    dietaryPreferences: '',
    startDate: '',
    planDuration: 'monthly',
    paymentMethod: 'credit_card',
    discountPercent: 0,
  });

  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (planId) {
      fetchMeals();
    }
  }, [planId]);

  const fetchMeals = async () => {
    try {
      setLoading(true);
      const data = await tiffinService.getMealsForPlan(planId);
      setMeals(data || []);
    } catch (err) {
      setError('Failed to load meals.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    try {
      setLoading(true);
      const subscriptionData = {
        planId: parseInt(planId),
        ...formData,
      };
      await tiffinService.subscribe(subscriptionData);
      setSuccess(true);
      setTimeout(() => {
        navigate('/tiffin/center');
      }, 2000);
    } catch (err) {
      setError('Failed to complete subscription. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container-custom max-w-4xl">
        <div className="mb-8">
          <button
            onClick={() => navigate('/tiffin/selection')}
            className="text-blue-600 hover:text-blue-800 mb-4 font-semibold"
          >
            ← Back to Selection
          </button>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Tiffin Subscription</h1>
          <p className="text-lg text-gray-600">
            Complete the form below to subscribe to our meal plan
          </p>
        </div>

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-green-800">
              Subscription successful! Redirecting...
            </p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="card">
          {/* Personal Information */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-6">Personal Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
              <div>
                <label className="form-label">Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
              <div>
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
              <div>
                <label className="form-label">Phone Number *</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="form-label">Billing Address *</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
              <div>
                <label className="form-label">City *</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
              <div>
                <label className="form-label">State *</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
              <div>
                <label className="form-label">Zip Code *</label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
            </div>
          </div>

          {/* Delivery Information */}
          <div className="mb-8 border-t pt-8">
            <h2 className="text-2xl font-bold mb-6">Delivery Information</h2>
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="form-label">Delivery Address *</label>
                <input
                  type="text"
                  name="deliveryAddress"
                  value={formData.deliveryAddress}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Leave blank if same as billing address"
                />
              </div>
              <div>
                <label className="form-label">Start Date *</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
              <div>
                <label className="form-label">Plan Duration *</label>
                <select
                  name="planDuration"
                  value={formData.planDuration}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
            </div>
          </div>

          {/* Dietary Preferences */}
          <div className="mb-8 border-t pt-8">
            <h2 className="text-2xl font-bold mb-6">Dietary Preferences</h2>
            <div>
              <label className="form-label">Special Dietary Preferences</label>
              <textarea
                name="dietaryPreferences"
                value={formData.dietaryPreferences}
                onChange={handleInputChange}
                className="form-input"
                rows="4"
                placeholder="Please describe any allergies, dietary restrictions, or preferences"
              />
            </div>
          </div>

          {/* Available Meals */}
          {meals.length > 0 && (
            <div className="mb-8 border-t pt-8">
              <h2 className="text-2xl font-bold mb-6">Available Meals</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {meals.map((meal) => (
                  <div key={meal.mealId} className="border rounded-lg p-4">
                    <h4 className="font-semibold text-lg mb-2">{meal.mealName}</h4>
                    <p className="text-gray-600 text-sm mb-2">{meal.mealDescription}</p>
                    <p className="text-sm text-gray-500">
                      <span className="font-semibold">Cuisine:</span> {meal.cuisineType}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payment Information */}
          <div className="mb-8 border-t pt-8">
            <h2 className="text-2xl font-bold mb-6">Payment Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">Payment Method *</label>
                <select
                  name="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                >
                  <option value="credit_card">Credit Card</option>
                  <option value="debit_card">Debit Card</option>
                  <option value="bank_transfer">Bank Transfer</option>
                </select>
              </div>
              <div>
                <label className="form-label">Discount Percent</label>
                <input
                  type="number"
                  name="discountPercent"
                  value={formData.discountPercent}
                  onChange={handleInputChange}
                  className="form-input"
                  min="0"
                  max="100"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="border-t pt-8">
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...' : 'Complete Subscription'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TiffinRegistration;
